import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from "react-redux";
import * as utilityActions from '../../redux/actions/utilityActions';
import * as userActions from '../../redux/actions/userActions';
import './Vignettes.scss';
import SupplimentaryPanel from './SupplimentaryPanel';
import CeAssets from '../../assets/img/CeAssets.png';
import { useToasts } from 'react-toast-notifications';
import APIConstant from '../../utility/APIConstant';
import Swal from 'sweetalert2';

const Vignettes = () => {
    const { addToast } = useToasts();
    const userInfo = useSelector(state => state?.user);
    const utility = useSelector(state => state.utility);
    const [selection, setSelection] = useState({ course: null, ids: [] });
    const [filter, setFilter] = useState('all');
    const dispatch = useDispatch();
    const assignedAids = useSelector(state => state?.user?.assignedAids);
    // the saved selection is applied once per course, so it cannot stamp over
    // changes made while the request was still in flight
    const hydrated = useRef(false);

    const courseId = utility?.selectedCourseUpdates?._id;

    /* the ids are held together with the course they belong to. the vignette
       list is the same for every course, so ids left over from a course we
       just left stay valid and used to be counted - and saved - against the
       new one. pairing them makes that impossible. */
    const selectedIds = useMemo(
        () => (selection.course === courseId ? selection.ids : []),
        [selection, courseId]
    );

    const setSelectedIds = useCallback((next) => {
        setSelection((prev) => {
            const base = prev.course === courseId ? prev.ids : [];
            return {
                course: courseId,
                ids: typeof next === 'function' ? next(base) : next,
            };
        });
    }, [courseId]);

    // the feed can repeat the same vignette, so keep the first of each id
    const Vignettes = useMemo(() => {
        const seen = new Set();
        return (userInfo?.supplementaries?.vignettes || []).filter((v) => {
            const key = v?._id;
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [userInfo?.supplementaries?.vignettes]);

    useEffect(() => {
        // Load selectedIds from session storage on mount
        const savedSelections = JSON.parse(sessionStorage.getItem('vignetteSelections')) || {};
        /* always replace. this used to set only when the new course had
           something saved, so opening a course with no selection of its own
           left the previous course's ticks on screen */
        setSelectedIds(courseId ? (savedSelections[courseId]?.vignette || []) : []);
        hydrated.current = false;
    }, [courseId, setSelectedIds]);

    // what is already assigned to this config, so the selection survives a
    // reload rather than only lasting the session
    useEffect(() => {
        if (courseId) dispatch(userActions.assignedAidsRequest(courseId));
    }, [courseId, dispatch]);

    useEffect(() => {
        if (hydrated.current) return;
        if (!assignedAids?.fetched || assignedAids.configId !== courseId) return;
        if (!Vignettes.length) return;

        /* the saved rows carry no _id, so match them back on path. the feed
           can hold several vignettes pointing at the same file, so take one
           card per saved row instead of every card sharing the path -
           otherwise one saved selection ticked two cards and the count
           doubled. */
        const saved = (assignedAids.vignettes || [])
            .map((v) => v?.path)
            .filter(Boolean);

        const byPath = new Map();
        Vignettes.forEach((v) => {
            const p = v?.content?.path;
            if (!p) return;
            if (!byPath.has(p)) byPath.set(p, []);
            byPath.get(p).push(v._id);
        });

        const taken = new Map();
        const ids = [];
        saved.forEach((p) => {
            const pool = byPath.get(p);
            if (!pool) return;
            const used = taken.get(p) || 0;
            if (used < pool.length) {
                ids.push(pool[used]);
                taken.set(p, used + 1);
            }
        });

        if (ids.length) setSelectedIds(ids);
        hydrated.current = true;
    }, [assignedAids, courseId, Vignettes, setSelectedIds]);

    // ids outlive the list they came from - session storage keeps the last
    // selection and the feed can change under it - so drop any id that has no
    // card. without this the count overstates the selection and Save posts ids
    // that are no longer real.
    useEffect(() => {
        if (!Vignettes.length) return;
        const live = new Set(Vignettes.map((v) => v?._id));
        setSelectedIds((prev) => {
            const next = Array.from(new Set(prev)).filter((id) => live.has(id));
            return next.length === prev.length ? prev : next;
        });
    }, [Vignettes, setSelectedIds]);

    useEffect(() => {
        /* only write ids that belong to this course. on a course switch the
           reset above has not landed yet, so the state still holds the old
           course's ids while courseId is already the new one - writing then
           filed the previous course's selection under the new course */
        if (!courseId || selection.course !== courseId) return;
        const savedSelections = JSON.parse(sessionStorage.getItem('vignetteSelections')) || {};
        savedSelections[courseId] = {
            ...savedSelections[courseId],
            vignette: selection.ids
        };
        sessionStorage.setItem('vignetteSelections', JSON.stringify(savedSelections));
    }, [selection, courseId]);

    // eslint-disable-next-line no-unused-vars
    const handleEdit = (item) => {
        dispatch(utilityActions.toggleSideSheet({
            direction: 'right',
            width: 800,
            title: 'Edit Vignette',
            open: true,
            component: <SupplimentaryPanel name={'vignettes'} mode={'Edit'} data={item} />
        }));
    };

    const handleSave = async () => {
        /* an empty save clears the course, so make that explicit rather than
           locking the button - without it there was no way to unassign
           everything once something had been assigned */
        if (!selectedIds.length) {
            const confirmed = await Swal.fire({
                title: 'Remove all vignettes?',
                text: 'Nothing is selected, so saving clears every vignette assigned to this course.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, clear them',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#d33',
            });
            if (!confirmed.isConfirmed) return;
        }
        let reqObj = {
            configid: courseId,
            vids: selectedIds,
            type: 9
        };
        dispatch(userActions.addConfigVignettesRequest(reqObj, addToast));
    };

    const allSelected = Vignettes.length > 0 && selectedIds.length === Vignettes.length;

    const handleSelectAll = () => {
        if (allSelected) {
            setSelectedIds([]); // Deselect all
            const savedSelections = JSON.parse(sessionStorage.getItem('vignetteSelections')) || {};
            savedSelections[courseId] = {
                ...savedSelections[courseId],
                vignette: []
            };
            sessionStorage.setItem('vignetteSelections', JSON.stringify(savedSelections));
        } else {
            setSelectedIds(Vignettes.map(v => v._id)); // Select all
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter(itemId => itemId !== id);
            }
            return [...prevSelected, id];
        });
    };

    /* with 130+ cards the only way to review a selection was to scroll the
       whole list looking for ticks */
    const visibleVignettes = useMemo(() => {
        if (filter === 'all') return Vignettes;
        const picked = new Set(selectedIds);
        return Vignettes.filter((v) =>
            filter === 'selected' ? picked.has(v?._id) : !picked.has(v?._id)
        );
    }, [Vignettes, selectedIds, filter]);

    return (
        <div className='vignettes-continer'>
            {!Vignettes?.length ? (
                <p className='no-data'>Currently there is no vignettes available...</p>
            ) : (
                <div className="vg-panel">
                    {/* stays put while the grid below scrolls */}
                    <div className="vg-bar">
                        <span className="vg-count">
                            {selectedIds.length
                                ? <><b>{selectedIds.length}</b> of {Vignettes.length} selected</>
                                : <>{Vignettes.length} vignettes</>}
                        </span>
                        <span className="vg-filter">
                            {[
                                { key: 'all', label: `All (${Vignettes.length})` },
                                { key: 'selected', label: `Selected (${selectedIds.length})` },
                                { key: 'unselected', label: `Unselected (${Vignettes.length - selectedIds.length})` },
                            ].map((f) => (
                                <button
                                    key={f.key}
                                    type="button"
                                    className={filter === f.key ? 'on' : ''}
                                    onClick={() => setFilter(f.key)}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </span>
                        <button
                            className={`vg-select-all ${allSelected ? 'on' : ''}`}
                            onClick={handleSelectAll}
                            type="button"
                        >
                            {allSelected ? 'Deselect All' : 'Select All'}
                        </button>
                    </div>

                    <div className="vg-scroll">
                        <div className="vg-grid">
                            {!visibleVignettes.length && (
                                <p className="vg-empty-filter">
                                    {filter === 'selected'
                                        ? 'Nothing selected yet.'
                                        : 'Everything is selected.'}
                                </p>
                            )}
                            {visibleVignettes.map((el) => {
                                const isSelected = selectedIds.includes(el?._id);
                                return (
                                    <div
                                        key={el?._id}
                                        className={`vg-card ${isSelected ? 'selected' : ''}`}
                                        onClick={() => toggleSelect(el?._id)}
                                        role="checkbox"
                                        aria-checked={isSelected}
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                toggleSelect(el?._id);
                                            }
                                        }}
                                    >
                                        <div className="vg-thumb">
                                            <img
                                                src={APIConstant?.getFilePath(el?.content?.thumb)}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = CeAssets;
                                                }}
                                                alt=""
                                            />
                                            {isSelected && <span className="vg-check">&#10003;</span>}
                                            <a
                                                href={el?.content?.path}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="vg-play"
                                                title="Play vignette"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                &#9654;
                                            </a>
                                        </div>
                                        <p className="vg-title" title={el?.content?.title}>
                                            {el?.content?.title}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* stays put so Save is always one click away */}
                    <div className="vg-foot">
                        <span className="vg-foot-note">
                            {selectedIds.length
                                ? `${selectedIds.length} selected`
                                : 'Nothing selected - saving clears every assigned vignette'}
                        </span>
                        <button
                            className="vg-save"
                            disabled={!Vignettes.length}
                            onClick={handleSave}
                            type="button"
                        >
                            Save
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Vignettes;
